/**
 * Broker-proposal calculations (used by Purchase + Refinance slide decks).
 *
 * Pure & stateless — given the proposal payload, returns the numbers each
 * slide needs (monthly/fortnightly/weekly repayments for the recommended
 * lender, totals, shortfall/surplus).
 *
 * Payload shape:
 *  {
 *    loanPurpose: 'purchase' | 'refinance',
 *    clientName, brokerName,
 *    currentLoanBalance, newLoanBalance,            // refinance-only
 *    lenders: [{name, rate, type, repayment, features:{offset,redraw,extraPayments}, recommended}],
 *    fundsRequired:  {purchasePrice, stampDuty, registration, legalFees, lenderFees, lmi, otherFees:[{name,amount}]},
 *    fundsAvailable: {loanAmount, deposit, gift, fhog, otherContributions},
 *    debts: [{type,name,amount}],                   // refinance-only
 *  }
 */

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const monthlyAmortPayment = (principal, annualRatePct, years) => {
  const P = num(principal);
  const r = num(annualRatePct) / 100 / 12;
  const n = num(years) * 12;
  if (!P || !n) return 0;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

export const computeBrokerProposal = (payload) => {
  const p = payload || {};
  const lenders = Array.isArray(p.lenders) ? p.lenders : [];
  const recommended = lenders.find((l) => l.recommended) || lenders[0] || null;

  // Totals from fundsRequired/fundsAvailable line items
  const fr = p.fundsRequired || {};
  const fa = p.fundsAvailable || {};

  let totalRequired = 0;
  if (p.loanPurpose === 'purchase') {
    totalRequired += num(fr.purchasePrice);
  } else if (p.loanPurpose === 'refinance') {
    totalRequired += num(p.currentLoanBalance);
    if (Array.isArray(p.debts)) {
      p.debts.forEach((d) => { totalRequired += num(d.amount); });
    }
  }
  totalRequired += num(fr.stampDuty)
    + num(fr.registration)
    + num(fr.legalFees)
    + num(fr.lenderFees)
    + num(fr.lmi);
  if (Array.isArray(fr.otherFees)) {
    fr.otherFees.forEach((f) => { totalRequired += num(f.amount); });
  }

  let totalAvailable = num(fa.loanAmount)
    + num(fa.deposit)
    + num(fa.gift)
    + num(fa.fhog)
    + num(fa.otherContributions);

  const difference = totalAvailable - totalRequired;

  // Repayments — use recommended lender's rate against the loan amount, 30yr term.
  const recommendedRate = recommended ? num(recommended.rate) : 0;
  const loanAmount = num(fa.loanAmount);
  const monthly = monthlyAmortPayment(loanAmount, recommendedRate, 30);
  const fortnightly = (monthly * 12) / 26;
  const weekly = (monthly * 12) / 52;

  const lenderRepayments = lenders.map((l) => {
    const m = monthlyAmortPayment(loanAmount, num(l.rate), 30);
    return {
      ...l,
      monthly: m,
      fortnightly: (m * 12) / 26,
      weekly: (m * 12) / 52,
    };
  });

  return {
    recommended,
    recommendedIndex: lenders.findIndex((l) => l.recommended),
    totalRequired,
    totalAvailable,
    difference,
    repayments: { monthly, fortnightly, weekly },
    lenderRepayments,
    loanAmount,
  };
};

export const formatCurrency = (n) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num(n));
